import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { BookingPaymentDialog, PaymentStatusChip, usePaymentStatuses, actionLabel } from "@/components/BookingPaymentPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { PartyPopper, Plus, Edit, Trash2, Download, Loader2, Mail, CheckCircle2, MessageSquare, BookHeart } from "lucide-react";
import { toast } from "sonner";
import MenuPicker from "@/components/booking/MenuPicker";
import { CostSummary } from "@/components/bi-shell/CostSummary";
import { CelebrationBuilder, CelebrationQuote, celebLines, celebRates, CELEB_SECTIONS } from "@/components/celebration/CelebrationBuilder";
import { selectionGaps, pkgPrice } from "@/components/catering/PackageBuilder";
import WhatsAppDialog from "@/components/booking/WhatsAppDialog";
import { isInternationalCenter } from "@/lib/api";
import { resolveCurrency } from "@/utils/money";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  "Birthday", "Anniversary", "Engagement", "Wedding",
  "Baby Shower / Godbharai", "Kids Party", "Corporate Event",
  "Kitty Party", "Women's Meet", "Just Get-together", "Other",
];
const OCCASIONS = ["Birthday", "Anniversary", "Baby Shower / Godbharai", "Kelvan", "Upanayan", "Family Gathering", "Other"];
const STATUSES = ["Quote", "Confirmed", "Cancelled", "Completed"];
const PAY_STATUSES = ["Pending", "Partial", "Paid"];
const MENU_CATS = ["Starters", "Main Course", "Roti / Bread", "Rice / Biryani", "Dal", "Vegetable", "Sweet / Dessert", "Salad / Raita"];
const DAL_OPTIONS = ["Dal Tadka", "Dal Fry", "Dal Makhani", "Yellow Dal", "Custom"];

const today = () => new Date().toISOString().split("T")[0];
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const blankForm = {
  enquiry_id: "",
  customer_name: "", phone: "", email: "",
  event_type: "Birthday", event_date: today(), time_slot: "",
  guest_count: 20, package_name: "",
  dal_selection: "Dal Tadka",
  menu_categories: {},
  decoration: "", special_rules: "",
  estimated_total: 0, gst: 0, final_total: 0,
  advance_paid: 0, balance_due: 0,
  package_id: "", per_person_rate: 0, menu_selections: {}, addons: {}, hall_charge: null,
  line_items: [], subtotal: 0, discount: 0, gst_mode: "exclusive", gst_rate: 0,
  confirmation_status: "Quote", payment_status: "Pending",
  quotation_link: "", assigned_manager: "",
  center: "",
};

export default function EventBookings({ wsCenter }) {
  const { session } = useAuth();
  const navigate = useNavigate();
  const token = session?.token;
  const [payRow, setPayRow] = useState(null);
  const [web, setWeb] = useState(null);
  const isAdmin = session?.is_super_admin || session?.is_admin;
  const userCenter = session?.center || "";

  const [rows, setRows] = useState([]);
  const [payMap, reloadPay] = usePaymentStatuses("CELEBRATION", rows.map((x) => x.id), session?.token);
  const [loading, setLoading] = useState(false);
  const [centers, setCenters] = useState([]);
  const [menuMaster, setMenuMaster] = useState([]);
  const [filters, setFilters] = useState({
    center: isAdmin ? "" : userCenter,
    from_date: "", to_date: "", status: "", search: "",
  });
  const [showDlg, setShowDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...blankForm, center: userCenter });
  const [waRow, setWaRow] = useState(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/sales/centers-list`).then(r => r.ok && r.json()).then((d) => {
      if (d?.centers) setCenters(d.centers);
    });
    fetch(`${API}/api/bookings/ext/event/website-config`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    }).then(r => r.ok && r.json()).then((d) => { if (d?.packages) setWeb(d); });
    fetch(`${API}/api/bookings/ext/menu/list`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    }).then(r => r.ok && r.json()).then((d) => {
      if (d?.items) setMenuMaster(d.items);
    });
  }, [token]);

  useEffect(() => {
    if (wsCenter !== undefined && isAdmin) setFilters((f) => (f.center === wsCenter ? f : { ...f, center: wsCenter }));
  }, [wsCenter, isAdmin]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/bookings/ext/event/list`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          centers: filters.center ? [filters.center] : [],
          from_date: filters.from_date || null,
          to_date: filters.to_date || null,
          status: filters.status || null,
          search: filters.search || null,
        }),
      });
      if (res.ok) setRows((await res.json()).rows || []);
    } finally { setLoading(false); }
  }, [token, filters]);

  useEffect(() => { load(); }, [load]);

  const recompute = (f) => {
    if (f.gst_mode === "exclusive") {
      const isIntlX = isInternationalCenter(f.center, centers);
      const line_items = celebLines(f);
      const subtotal = round2(Math.max(line_items.reduce((s, x) => s + x.amount, 0) - (Number(f.discount) || 0), 0));
      const rateX = isIntlX ? 10 : 5;
      const gstX = round2(subtotal * rateX / 100);
      const final_total = round2(subtotal + gstX);
      return { ...f, line_items, subtotal, estimated_total: subtotal, gst_rate: rateX, gst: gstX, final_total, balance_due: round2(final_total - (Number(f.advance_paid) || 0)) };
    }
    let lineTotal = 0;
    Object.values(f.menu_categories || {}).forEach((rows) => {
      rows.forEach((r) => { lineTotal += Number(r.amount) || 0; });
    });
    const finalTotal = round2(lineTotal);
    const isIntl = isInternationalCenter(f.center, centers);
    const rate = isIntl ? 0.10 : 0.05;
    const gst = round2(finalTotal - finalTotal / (1 + rate));
    const estimatedTotal = round2(finalTotal - gst);
    const balance_due = round2(finalTotal - (Number(f.advance_paid) || 0));
    return { ...f, estimated_total: estimatedTotal, gst, final_total: finalTotal, balance_due };
  };

  useEffect(() => {
    setForm((f) => recompute(f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.menu_categories, form.advance_paid, form.center, centers, form.gst_mode, form.guest_count, form.per_person_rate,
      form.hall_charge, form.discount, JSON.stringify(form.addons)]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (row) => {
    if (row) { setEditing(row); setForm(recompute({ ...blankForm, gst_mode: "", ...row })); }
    else { setEditing(null); setForm(recompute({ ...blankForm, center: wsCenter || userCenter })); }
    setShowDlg(true);
  };

  const save = async () => {
    if (!form.customer_name || !form.phone) { toast.error("Customer name and phone required"); return; }
    if (!form.center) { toast.error("Center required"); return; }
    const url = editing
      ? `${API}/api/bookings/ext/event/update/${editing.id}`
      : `${API}/api/bookings/ext/event/create`;
    const payload = recompute(form);
    if (payload.gst_mode === "exclusive") {
      if (!payload.package_id) { toast.error("Select a thali package"); return; }
      const gaps = selectionGaps(web?.packages?.find((p) => p.id === payload.package_id), payload.menu_selections, CELEB_SECTIONS);
      if (gaps.length) toast.warning(`Menu not complete: ${gaps.map(([l, n, need]) => `${l.split(" (")[0]} ${n}/${need}`).join(", ")}`);
    }
    const res = await fetch(url, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, ...payload }),
    });
    if (res.ok) {
      toast.success(editing ? "Updated" : "Event booking created");
      window.dispatchEvent(new Event("bi-refresh"));
      setShowDlg(false); load();
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete event ${row.id}?`)) return;
    const res = await fetch(`${API}/api/bookings/ext/event/delete/${row.id}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    });
    if (res.ok) { toast.success("Deleted"); load(); }
  };

  const downloadPdf = async (row) => {
    const res = await fetch(`${API}/api/bookings/ext/event/pdf/${row.id}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token }),
    });
    if (!res.ok) { toast.error("PDF failed"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `event_${row.id}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const sendQuote = async (row) => {
    if (!row.email) {
      const m = window.prompt("Customer email not set. Enter email to send to:");
      if (!m) return;
      row = { ...row, email: m };
    }
    const t = toast.loading(`Emailing quote to ${row.email}...`);
    try {
      const res = await fetch(`${API}/api/bookings/ext/event/send-quote/${row.id}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, to_email: row.email }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(`Quote sent to ${d.to} (${d.size_kb} KB)`, { id: t });
        load();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || "Send failed", { id: t });
      }
    } catch (err) {
      toast.error("Network error", { id: t });
    }
  };

  const masterByCat = menuMaster.reduce((acc, m) => {
    (acc[m.category] = acc[m.category] || []).push(m); return acc;
  }, {});

  // Map EventBookings event_type → Memory Box occasion taxonomy
  const eventTypeToOccasion = (t) => {
    const x = (t || '').toLowerCase();
    if (x.includes('birth')) return 'Birthday';
    if (x.includes('anniv')) return 'Anniversary';
    if (x.includes('baby')) return 'Baby Shower';
    if (x.includes('dohal')) return 'Dohal Jevan';
    if (x.includes('upan') || x.includes('thread')) return 'Upanayan';
    if (x.includes('naming')) return 'Naming Ceremony';
    if (x.includes('retire')) return 'Retirement Function';
    if (x.includes('corp')) return 'Corporate Event';
    if (x.includes('wedding') || x.includes('engagement') || x.includes('reception')) return 'Family Gathering';
    return 'Family Gathering';
  };

  const openMemoryBox = (row) => {
    try {
      localStorage.setItem('mbox_prefill', JSON.stringify({
        source: 'Event Booking',
        center: row.center || userCenter,
        guest_name: row.customer_name || '',
        mobile: row.phone || '',
        email: row.email || '',
        event_date: row.event_date || today(),
        order_number: row.id || '',
        occasion: eventTypeToOccasion(row.event_type),
        celebration_for: row.event_type || '',
      }));
      navigate('/ad-creator?tab=memory-box');
    } catch (e) {
      toast.error('Could not prefill Memory Box');
    }
  };

  const isIntl = isInternationalCenter(form.center, centers);
  const _ebCenterDoc = (centers || []).find(c => c?.code === form.center);
  const currency = _ebCenterDoc ? resolveCurrency(_ebCenterDoc).symbol : (isIntl ? "$" : "₹");
  const gstLabel = isIntl ? "10% GST (incl.)" : "5% GST (incl.)";
  // Centre country changed after a package was picked → re-apply that country's website prices
  const centreRef = useRef({ id: null, center: null });
  useEffect(() => {
    const key = editing?.id || "new";
    const prev = centreRef.current; centreRef.current = { id: key, center: form.center };
    if (prev.id !== key || prev.center === form.center || prev.center === null) return;
    const p = web?.packages?.find((x) => x.id === form.package_id);
    if (form.gst_mode !== "exclusive" || !p) return;
    const r = celebRates(web.rates, isIntl);
    setForm((f) => ({ ...f, per_person_rate: pkgPrice(p, isIntl), hall_charge: r.hall,
      addons: { ...(f.addons || {}), breakfast_rate: r.breakfast, snacks_rate: r.snacks,
        drinks_rate: f.addons?.drinks_mode === "half_day" ? r.half : f.addons?.drinks_mode === "full_day" ? r.full : 0,
        drinks_items: (f.addons?.drinks_items || []).map((d) => { const o = web.drinks_options.find((x) => x.id === d.id); return o ? { ...d, price: Number(isIntl ? o.price_aud : o.price_inr) || 0 } : d; }) } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.center, editing?.id]);

  return (
    <div className="space-y-6" data-testid="event-bookings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <PartyPopper className="w-7 h-7" />Event / Celebration Bookings
          </h1>
          <p className="text-muted-foreground text-sm">
            Birthdays, anniversaries, weddings, corporate. Website thali packages, add-ons &amp; live quotation (GST added on top). Older quotes keep their GST-included totals.
          </p>
        </div>
        <Button onClick={() => open(null)} data-testid="event-new-btn">
          <Plus className="w-4 h-4 mr-2" /> New Event Booking
        </Button>
      </div>

      <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {isAdmin && (
          <div><Label className="text-xs">Center</Label>
            <Select value={filters.center || "all"} onValueChange={(v) => setFilters(f => ({ ...f, center: v === "all" ? "" : v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Centers</SelectItem>
                {centers.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div><Label className="text-xs">From</Label>
          <Input className="h-9" type="date" value={filters.from_date}
            onChange={(e) => setFilters(f => ({ ...f, from_date: e.target.value }))} /></div>
        <div><Label className="text-xs">To</Label>
          <Input className="h-9" type="date" value={filters.to_date}
            onChange={(e) => setFilters(f => ({ ...f, to_date: e.target.value }))} /></div>
        <div><Label className="text-xs">Status</Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Search</Label>
          <Input className="h-9" placeholder="Name / Phone / ID..." value={filters.search}
            onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))} />
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">No event bookings.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead><TableHead>Customer</TableHead>
                <TableHead>Event</TableHead><TableHead>Guests</TableHead>
                <TableHead>Final Total</TableHead><TableHead>Status</TableHead>
                <TableHead>Center</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`event-row-${r.id}`}>
                  <TableCell className="text-xs">{r.id}</TableCell>
                  <TableCell>
                    <div className="font-medium">{r.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{r.phone}</div>
                  </TableCell>
                  <TableCell className="text-xs">{r.event_date}<br />{r.time_slot}<br />
                    <Badge variant="outline">{r.event_type}</Badge>
                  </TableCell>
                  <TableCell>{r.guest_count}</TableCell>
                  <TableCell>{Number(r.final_total || 0).toFixed(2)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{r.confirmation_status}</Badge>
                    <div className="text-xs mt-1">{r.payment_status}</div>
                    <div className="mt-1"><PaymentStatusChip st={payMap[r.id]} testid={`event-pay-status-${r.id}`} /></div>
                  </TableCell>
                  <TableCell className="text-xs">{r.center}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => open(r)} title="Edit"><Edit className="w-4 h-4" /></Button>
                      <button type="button" className="bi-pill !py-1 !px-2 !text-xs whitespace-nowrap" onClick={() => setPayRow(r)} data-testid={`event-pay-btn-${r.id}`}>{actionLabel(payMap[r.id])}</button>
                      <Button size="icon" variant="ghost" onClick={() => downloadPdf(r)} title="Download PDF"><Download className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => sendQuote(r)} title="Send quote to customer" data-testid={`event-send-quote-${r.id}`}>
                        {r.quote_sent_at
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          : <Mail className="w-4 h-4 text-blue-500" />}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setWaRow(r)} title="WhatsApp"
                        data-testid={`event-whatsapp-${r.id}`}
                        className={r.whatsapp_sent_at ? "text-emerald-500" : "text-green-500"}>
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openMemoryBox(r)}
                        title="Create Memory Box for this guest"
                        data-testid={`event-memory-box-${r.id}`}
                        className="text-rose-700 hover:text-rose-900">
                        <BookHeart className="w-4 h-4" />
                      </Button>
                      {isAdmin && <Button size="icon" variant="ghost" onClick={() => del(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <BookingPaymentDialog open={!!payRow} onClose={() => { setPayRow(null); load(); }} session={session} bookingType="CELEBRATION" bookingId={payRow?.id} onChanged={reloadPay} />
      <Dialog open={showDlg} onOpenChange={setShowDlg}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.id}` : "New Celebration at PB"}</DialogTitle></DialogHeader>
          <div className="space-y-4" style={{ "--bi-accent": "#62115a" }}>
            {form.gst_mode !== "exclusive" && <p className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="event-legacy-note">
              This quote was made before website packages were added (GST included). Picking a package below switches it to the website quotation (GST added on top).</p>}
            {web ? <CelebrationBuilder form={form} setForm={setForm} web={web} intl={isIntl} sym={currency} />
              : <p className="text-sm text-muted-foreground" data-testid="event-pkg-loading">Loading website packages…</p>}
            <section className="bi-step" data-testid="event-step-occasion">
              <h3 className="bi-step-title"><span className="bi-step-num">4</span>Guests, occasion &amp; date</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {OCCASIONS.map((o) => (
                  <button key={o} type="button" onClick={() => setForm(f => ({ ...f, event_type: o }))}
                    className={`rounded-2xl border-2 px-3 py-4 text-base font-semibold transition-colors ${form.event_type === o ? "border-[#62115a] bg-[#62115a] text-[#fff3c4]" : "border-[#e2b85a] bg-white text-[#62115a] hover:bg-[#fbeef7]"}`}
                    data-testid={`event-occasion-${o.replace(/[^a-z]/gi, "")}`}>{o}</button>))}
              </div>
              {!OCCASIONS.includes(form.event_type) && <p className="mt-2 text-sm text-muted-foreground">Saved occasion: {form.event_type}</p>}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {isAdmin && (
                  <div>
                    <Label>Centre *</Label>
                    <Select value={form.center} onValueChange={(v) => setForm(f => ({ ...f, center: v }))}>
                      <SelectTrigger data-testid="event-centre"><SelectValue placeholder="Pick centre..." /></SelectTrigger>
                      <SelectContent>{centers.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Date *</Label><Input type="date" value={form.event_date} onChange={(e) => setForm(f => ({ ...f, event_date: e.target.value }))} data-testid="event-date" /></div>
                <div><Label>Time</Label><Input value={form.time_slot} placeholder="7:00 PM - 11:00 PM" onChange={(e) => setForm(f => ({ ...f, time_slot: e.target.value }))} data-testid="event-time" /></div>
                <div><Label>Guest count</Label><Input type="number" value={form.guest_count} onChange={(e) => setForm(f => ({ ...f, guest_count: Number(e.target.value) || 0 }))} data-testid="event-guests" /></div>
              </div>
            </section>

            {form.gst_mode !== "exclusive" && <section className="bi-step" data-testid="event-step-food">
              <h3 className="bi-step-title"><span className="bi-step-num">2</span>Food package / menu</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Package / thali</Label><Input value={form.package_name} onChange={(e) => setForm(f => ({ ...f, package_name: e.target.value }))} placeholder="Celebration Thali Premium / Basic..." data-testid="event-package" /></div>
                <div><Label>Dal</Label>
                  <Select value={form.dal_selection} onValueChange={(v) => setForm(f => ({ ...f, dal_selection: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{DAL_OPTIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 space-y-3">
                {MENU_CATS.map((cat) => (
                  <MenuPicker key={cat}
                    category={cat}
                    rows={form.menu_categories[cat] || []}
                    onChange={(rows) => setForm(f => ({
                      ...f, menu_categories: { ...f.menu_categories, [cat]: rows },
                    }))}
                    master={masterByCat[cat] || []}
                    testIdPrefix={`event-${cat.replace(/[^a-z]/gi, "")}`} />
                ))}
              </div>
            </section>}

            {form.gst_mode === "exclusive" ? (
              <section className="bi-step" data-testid="event-step-quote">
                <h3 className="bi-step-title"><span className="bi-step-num">5</span>Quotation</h3>
                <CelebrationQuote form={form} setForm={setForm} sym={currency} />
              </section>
            ) : <section className="bi-step" data-testid="event-step-quote">
              <h3 className="bi-step-title"><span className="bi-step-num">3</span>Estimated cost</h3>
              <CostSummary sym={currency} testid="event-cost"
                rows={[["Estimated cost (before GST)", form.estimated_total, "event-estimated"], [gstLabel, form.gst, "event-gst"]]}
                total={["Estimated total", form.final_total]} />
              <div className="text-xs text-muted-foreground italic mt-2">GST included in the total (same calculation as before: {isIntl ? "Australia 10%" : "India 5%"}).</div>
              <span className="hidden" data-testid="event-final-total">{form.final_total.toFixed(2)}</span>
            </section>}

            <section className="bi-step" data-testid="event-step-customer">
              <h3 className="bi-step-title"><span className="bi-step-num">{form.gst_mode === "exclusive" ? 6 : 4}</span>Guest details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Customer name *</Label><Input value={form.customer_name} onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))} data-testid="event-customer-name" /></div>
                <div><Label>Phone *</Label><Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="event-phone" /></div>
              </div>
            </section>

            <details className="bi-step" data-testid="event-more">
              <summary className="cursor-pointer text-base font-semibold text-[#62115a]">More details (decoration, payment, notes)</summary>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} /></div>
                <div><Label>Enquiry ID</Label><Input value={form.enquiry_id} onChange={(e) => setForm(f => ({ ...f, enquiry_id: e.target.value }))} /></div>
                <div><Label>Assigned manager</Label><Input value={form.assigned_manager} onChange={(e) => setForm(f => ({ ...f, assigned_manager: e.target.value }))} /></div>
                <div><Label>Quotation link</Label><Input value={form.quotation_link} onChange={(e) => setForm(f => ({ ...f, quotation_link: e.target.value }))} /></div>
                <div><Label>Decoration</Label><Textarea rows={2} value={form.decoration} onChange={(e) => setForm(f => ({ ...f, decoration: e.target.value }))} /></div>
                <div><Label>Special rules / notes</Label><Textarea rows={2} value={form.special_rules} onChange={(e) => setForm(f => ({ ...f, special_rules: e.target.value }))} /></div>
                <div><Label>Advance paid</Label><Input type="number" value={form.advance_paid} onChange={(e) => setForm(f => ({ ...f, advance_paid: Number(e.target.value) || 0 }))} /></div>
                <div className="flex items-end justify-between text-base"><span>Balance due</span><b>{currency}{form.balance_due.toFixed(2)}</b></div>
                <div><Label>Confirmation status</Label>
                  <Select value={form.confirmation_status} onValueChange={(v) => setForm(f => ({ ...f, confirmation_status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Payment status</Label>
                  <Select value={form.payment_status} onValueChange={(v) => setForm(f => ({ ...f, payment_status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </details>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowDlg(false)}>Cancel</Button>
            {editing && <Button variant="outline" onClick={() => downloadPdf(editing)} data-testid="event-draft-pdf"><Download className="w-4 h-4 mr-2" />Download quotation PDF</Button>}
            {editing && <Button variant="outline" className="text-green-700" onClick={() => { setShowDlg(false); setWaRow(editing); }} data-testid="event-share-wa"><MessageSquare className="w-4 h-4 mr-2" />Share on WhatsApp</Button>}
            <Button onClick={save} data-testid="event-save-btn">{editing ? "Save changes" : "Save Enquiry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WhatsAppDialog
        open={!!waRow}
        onOpenChange={(v) => !v && setWaRow(null)}
        kind="event"
        row={waRow}
        token={token}
        onMarkedSent={load}
      />
    </div>
  );
}
